import React, { useState } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import person from '../assets/person.png';

// Font imports
import '@fontsource/lora/400.css';
import '@fontsource/lora/600.css';
import '@fontsource/lora/700.css';
import '@fontsource/source-sans-pro/400.css';
import '@fontsource/source-sans-pro/600.css';

// Theme styles
const theme = {
  colors: {
    bg: {
      primary: "bg-emerald-50", // Light sage background
      secondary: "bg-gray-50", // Light gray
      card: "bg-white", // White
      accent: "bg-emerald-600", // Primary green
      accent2: "bg-emerald-700", // Darker green for hover
      button: {
        primary: "bg-emerald-600 hover:bg-emerald-700",
        secondary: "bg-white hover:bg-gray-50 border border-emerald-600"
      },
      sage: {
        light: "bg-emerald-50",
        medium: "bg-emerald-50",
        dark: "bg-emerald-100",
      }
    },
    text: {
      heading: "text-slate-700", // Dark slate for headings
      body: "text-slate-600", // Lighter slate for body
      primary: "text-slate-700", // Same as heading
      secondary: "text-slate-600", // Same as body
      muted: "text-slate-500", // Even lighter
      dimmed: "text-slate-400", // Lightest
      accent: "text-emerald-600", // Keep accent green
      button: {
        primary: "text-white",
        secondary: "text-emerald-600 hover:text-emerald-700"
      }
    },
    border: {
      subtle: "border-gray-200",
      hover: "hover:border-emerald-600"
    }
  },
  typography: {
    heading: "font-['Lora']",
    body: "font-['Source Sans Pro']"
  }
};

const OriginalLandingPage: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  return (
    <div className={`min-h-screen bg-[${theme.colors.bg.primary}] relative overflow-hidden ${theme.typography.body} text-[${theme.colors.text.body}]`}>
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 bg-white ${theme.colors.border.subtle}`}>
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className={`text-2xl ${theme.typography.heading} font-medium text-[${theme.colors.text.heading}]`}>
              Evida
            </div>
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className={`md:hidden text-[${theme.colors.text.body}] hover:text-[${theme.colors.text.heading}]`}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
            <div className="hidden md:flex items-center gap-8">
              <a href="https://security.evida.ai/" className="text-slate-600 hover:text-slate-800 transition-colors duration-200">Security</a>
              {/*<button className="text-slate-600 hover:text-slate-800 font-medium transition-colors duration-200">
                Sign in
              </button>*/}
              <a href="https://zcal.co/neilpatil/evida" target="_blank" rel="noopener noreferrer" className="bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 rounded-lg transition-all duration-200 text-center">
                Get started
              </a>
            </div>
          </div>

          {/* Mobile menu */}
          <div className={`${isMenuOpen ? 'flex' : 'hidden'} md:hidden flex-col gap-4 pt-4 pb-2`}>
            <a href="#product" className="text-slate-600 hover:text-slate-800 transition-colors duration-200">Product</a>
            <a href="#pricing" className="text-slate-600 hover:text-slate-800 transition-colors duration-200">Pricing</a>
            <a href="#security" className="text-slate-600 hover:text-slate-800 transition-colors duration-200">Security</a>
            <a href="#docs" className="text-slate-600 hover:text-slate-800 transition-colors duration-200">Documentation</a>
            <div className="flex flex-col gap-4 pt-2">
              <a href="https://dev.evida.io" className="text-slate-600 hover:text-slate-800 font-medium transition-colors duration-200 text-left">
                Sign in
              </a>
              <a href="https://zcal.co/neilpatil/evida" target="_blank" rel="noopener noreferrer" className="bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 rounded-lg transition-all duration-200 text-center">
                Get started
              </a>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-20 md:pt-28 pb-4 px-4 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-center">
            {/* Hero Image */}
            <div className="w-full md:w-1/2 mb-8 md:mb-0 md:order-2">
              <img
                src={person}
                alt="Healthcare professional using Evida"
                className="w-full h-auto object-contain"
              />
            </div>

            <div className="w-full md:w-1/2 pr-0 md:pr-8 md:order-1">
              <h1 className={`text-4xl md:text-5xl ${theme.typography.heading} font-medium pb-2 mb-8 md:mb-8 text-slate-900`}>
                AI chart review built to protect your revenue
              </h1>
              <p className="text-lg md:text-xl ${theme.typography.body} text-slate-600 leading-relaxed pb-4">
                Evida audits every chart pre-bill to optimize billing and compliance, find missing revenue, and reduce denial and clawback risk.
              </p>
              <p className="text-lg md:text-xl ${theme.typography.body} text-slate-600 mb-6 md:mb-12 leading-relaxed">
                Save hundreds of hours on chart audits, improve care quality, and increase reimbursement revenue by up to 15%.
              </p>

              <div className="flex flex-col sm:flex-row gap-4">
                <a
                  href="https://zcal.co/neilpatil/evida"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full sm:w-auto bg-emerald-600 hover:bg-emerald-700 text-white px-6 py-3 rounded-lg font-medium transition-all duration-200 text-center"
                >
                  Request a demo →
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Indicators */}
      <section className={`py-16 px-4 bg-emerald-50 relative overflow-hidden`}>
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {[
              {
                title: "Seamless integration",
                description: "Sits within your existing EHR and billing flows.",
                icon: (
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                )
              },
              {
                title: "ROI guarantee",
                description: "Only pay for approved findings. If a payor rejects a Evida-audited chart, we cover it.",
                icon: (
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                )
              },
              {
                title: "Built by compliance experts",
                description: "Founded by engineers who built the first automated HIPAA compliance platform.",
                icon: (
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
                  </svg>
                )
              }
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.4, ease: "easeOut" }}
                className="flex items-start gap-4 group"
              >
                <div className={`w-12 h-12 rounded-lg bg-[${theme.colors.bg.primary}] flex items-center justify-center flex-shrink-0 group-hover:bg-[${theme.colors.bg.accent}]/10 transition-colors`}>
                  <div className={`text-[${theme.colors.text.accent}]`}>
                    {item.icon}
                  </div>
                </div>
                <div>
                  <h3 className={`text-xl ${theme.typography.heading} font-medium text-[${theme.colors.text.heading}] mb-2 group-hover:text-[${theme.colors.text.accent}] transition-colors`}>
                    {item.title}
                  </h3>
                  <p className={`${theme.typography.body} text-[${theme.colors.text.body}] group-hover:text-[${theme.colors.text.secondary}] transition-colors`}>
                    {item.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why use Evida */}
      <section className="py-16 px-4 relative overflow-hidden bg-gradient-to-br from-emerald-50 to-sky-50">
        <div className="max-w-7xl mx-auto relative z-10">
          <h3 className={`text-2xl md:text-3xl text-center max-w-4xl mx-auto mb-8 text-[${theme.colors.text.heading}] ${theme.typography.heading} font-medium`}>
            Up to 15% of clinical revenue leaks from avoidable chart gaps - undercoding, compliance issues, or missing evidence that cause clawbacks and denials.
          </h3>
          <h3 className={`text-2xl md:text-3xl text-center max-w-4xl mx-auto mb-16 text-[${theme.colors.text.heading}] ${theme.typography.heading}`}>
            Evida reviews 100% of charts at sign-off and pre-bill, helping providers and audit teams catch and fix these in one click.
          </h3>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-12 max-w-6xl mx-auto">
        {[
          {
            title: "Grow revenue",
            description: "Increase revenue per patient by up to 15% by identifying missed coding or care opportunities. Pay only for approved findings, with guaranteed ROI.",
            icon: (
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            ),
            bgColor: "bg-emerald-100"
          },
          {
            title: "Prevent denials",
            description: "Prevent revenue leakage at the source by autonomously reviewing every chart and claim for potential denial risks.",
            icon: (
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            ),
            bgColor: "bg-sky-100"
          },
          {
            title: "Streamline compliance",
            description: "Audit 100% of charts and auto-generate comprehensive audit trails and appeal packets.",
            icon: (
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            ),
            bgColor: "bg-amber-100"
          }
        ].map((item, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: index * 0.1 }}
            className="flex flex-col items-center text-center group"
          >
            <div className={`w-20 h-20 ${item.bgColor} rounded-full flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-200`}>
              <div className={`text-[${theme.colors.text.accent}]`}>
                {item.icon}
              </div>
            </div>
            <h3 className={`text-xl ${theme.typography.heading} font-medium mb-4 text-[${theme.colors.text.heading}] group-hover:text-[${theme.colors.text.accent}] transition-colors`}>
              {item.title}
            </h3>
            <p className={`${theme.typography.body} text-[${theme.colors.text.body}] leading-relaxed`}>
              {item.description}
            </p>
          </motion.div>
        ))}
      </div>
    </div>
      </section >

  {/* How it Works */ }
  <section className="py-24 px-4 bg-gray-50 relative">
    <div className="max-w-7xl mx-auto">
      <motion.h2
        initial={{ opacity: 0, y: 10 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-50px" }}
        transition={{ duration: 0.4, ease: "easeOut" }}
        className={`text-4xl ${theme.typography.heading} font-medium text-center mb-20 text-[${theme.colors.text.heading}]`}
      >
        How it works
      </motion.h2>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        {/* Steps */}
        <div className="md:col-span-2 space-y-6">
          {[
            {
              icon: (
                <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
                </svg>
              ),
              title: "Scans every chart",
              description: "find issues as soon as they're entered in Athena, eCW, Epic, etc."
            },
            {
              icon: (
                <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
                </svg>
              ),
              title: "Real-time AI audit",
              description: "automatically check against payor contracts, ICD ⇄ CPT logic, and HCC & quality measures"
            },
            {
              icon: (
                <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              ),
              title: "Dashboard for RCM / audit leads",
              description: "see every issue ranked by $ impact & compliance risk."
            },
            {
              icon: (
                <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              ),
              title: "One-click provider prompt",
              description: "optionally deploy a copilot into your EHR to guide providers towards fixes"
            },
            {
              icon: (
                <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              ),
              title: "Immutable audit trail",
              description: "maintain evidence to defend against insurance clawbacks and quality audits"
            }
          ].map((step, index) => (
            <motion.div
              key={index}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              variants={{
                hidden: { opacity: 0, y: 20 },
                visible: {
                  opacity: 1,
                  y: 0,
                  transition: {
                    duration: 0.5,
                    delay: index * 0.1
                  }
                }
              }}
              className="relative"
            >
              <div className="flex items-start gap-6 bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow duration-200">
                <div className="flex-shrink-0">
                  <div className={`w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center text-emerald-600`}>
                    {step.icon}
                  </div>
                </div>
                <div className="flex-1">
                  <h3 className={`text-lg font-normal ${theme.typography.heading} mb-2 text-[${theme.colors.text.heading}]`}>
                    {step.title}
                  </h3>
                  <p className={`text-sm text-[${theme.colors.text.muted}] leading-relaxed`}>
                    {step.description}
                  </p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Floating Card */}
        <div className="hidden md:block relative">
          <motion.div
            initial={{ opacity: 0, y: 20, rotate: 2 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            className={`
                  sticky top-20 w-full max-w-lg 
                  bg-white 
                  rounded-2xl shadow-xl 
                  p-6 md:p-8 
                  transform translate-x-0 md:translate-x-12 mx-4 md:mx-0 
                  border border-emerald-100
                `}
          >
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-emerald-600" viewBox="0 0 20 20" fill="currentColor">
                    <path d="M2 11a1 1 0 011-1h2a1 1 0 011 1v5a1 1 0 01-1 1H3a1 1 0 01-1-1v-5zM8 7a1 1 0 011-1h2a1 1 0 011 1v9a1 1 0 01-1 1H9a1 1 0 01-1-1V7zM14 4a1 1 0 011-1h2a1 1 0 011 1v12a1 1 0 01-1 1h-2a1 1 0 01-1-1V4z" />
                  </svg>
                </div>
                <div>
                  <div className="text-sm font-semibold text-slate-800">Patient visit summary</div>
                  <div className="text-xs text-slate-600">Annual Physical - 10/12/2024</div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></div>
                <div className="text-sm text-slate-600">Live</div>
              </div>
            </div>
            <div className="space-y-6">
              <div className="space-y-4">
                <div className="h-2 bg-slate-100 rounded-full w-3/4"></div>
                <div className="h-2 bg-slate-100 rounded-full w-1/2"></div>
                <div className="h-2 bg-slate-100 rounded-full w-5/6"></div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-slate-50 rounded-lg p-3 border border-slate-200">
                  <div className="text-xs text-slate-600 mb-1">Reviewed</div>
                  <div className="text-sm font-semibold text-slate-800">1,240 charts</div>
                </div>
                <div className="bg-slate-50 rounded-lg p-3 border border-slate-200">
                  <div className="text-xs text-slate-600 mb-1">Issues Fixed</div>
                  <div className="text-sm font-semibold text-slate-800">20 major</div>
                </div>
                <div className="bg-slate-50 rounded-lg p-3 border border-slate-200">
                  <div className="text-xs text-slate-600 mb-1">Time Saved</div>
                  <div className="text-sm font-semibold text-slate-800">35 hours</div>
                </div>
              </div>
              <div className="bg-emerald-50 rounded-lg p-4 border border-emerald-100">
                <div className="text-sm text-slate-700 mb-1">Estimated Revenue Impact</div>
                <div className="text-2xl font-bold text-emerald-600">+$25,610</div>
                <div className="text-xs text-slate-500">Based on improved documentation and coding</div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  </section>

  {/* Rollout Section */ }
  <section className="py-24 pb-12 px-4 bg-emerald-50">
    <div className="max-w-7xl mx-auto">
      <motion.h2
        initial={{ opacity: 0, y: 10 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-50px" }}
        transition={{ duration: 0.4, ease: "easeOut" }}
        className={`text-4xl ${theme.typography.heading} font-medium text-center mb-20 text-[${theme.colors.text.heading}]`}
      >
        See the impact before spending a cent
      </motion.h2>


      <div className="grid grid-cols-1 md:grid-cols-2 gap-12 max-w-5xl mx-auto">
        {/* Step 1 Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className={`bg-white rounded-2xl shadow-lg overflow-hidden border-2 border-emerald-100 hover:border-emerald-200 transition-all duration-300`}
        >
          <div className={`p-8`}>
            <div className="flex items-center mb-6">
              <div className="bg-emerald-100 rounded-full w-12 h-12 mr-4 flex items-center justify-center">
                <span className="text-2xl font-bold text-emerald-600">1</span>
              </div>
              <h3 className={`text-xl font-medium ${theme.typography.heading} text-[${theme.colors.text.heading}]`}>
                Start with a free look-back audit
              </h3>
            </div>
            <div className="space-y-4 text-lg">
              <p className={`text-[${theme.colors.text.body}]`}>
                Get started with one-click EHR integration or chart upload. We'll review up to 500 charts for coding gaps and compliance risks.
              </p>
            </div>
          </div>
        </motion.div>

        {/* Step 2 Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className={`bg-white rounded-2xl shadow-lg overflow-hidden border-2 border-emerald-100 hover:border-emerald-200 transition-all duration-300`}
        >
          <div className={`p-8`}>
            <div className="flex items-center mb-6">
              <div className="bg-emerald-100 rounded-full w-12 h-12 mr-4 flex items-center justify-center">
                <span className="text-2xl font-bold text-emerald-600">2</span>
              </div>
              <h3 className={`text-xl font-medium ${theme.typography.heading} text-[${theme.colors.text.heading}]`}>
                Then enable seamless live review
              </h3>
            </div>
            <div className="space-y-4 text-lg">
              <p className={`text-[${theme.colors.text.body}]`}>
                If you see the ROI, we'll audit and fix additional encounters in real time. You only pay for provider-approved incremental collections.
              </p>
            </div>
          </div>
        </motion.div>
      </div>

      <div className="mt-16 text-center">
        <a
          href="https://zcal.co/neilpatil/evida"
          target="_blank"
          rel="noopener noreferrer"
          className="bg-emerald-600 hover:bg-emerald-700 text-white px-6 py-3 rounded-lg font-medium transition-all duration-200 inline-flex items-center gap-2"
        >
          Start Your Free Pilot
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" />
          </svg>
        </a>
      </div>
    </div>
  </section>

  {/* Testimonial */ }
  {/*< section className = "py-20 px-4 bg-emerald-50 relative overflow-hidden" >
    <div className="max-w-3xl mx-auto text-center relative z-10">
      <div className="text-slate-700">
        <blockquote className={`text-2xl ${theme.typography.heading} font-medium mb-8 text-slate-700`}>
          "Evida feels like having a veteran auditor on every chart."
        </blockquote>
        <p className="text-slate-600 font-medium">— COO, mid-size outpatient group</p>
      </div>
    </div>
    <div className="max-w-3xl mx-auto pt-24">
      <p className={`text-sm text-[${theme.colors.text.body}] text-center mb-8`}>
        U.S. hosting • HIPAA, SOC 2 & ISO 27001 certified • Copyright 2025 Syntra, Inc.
      </p>
    </div>

      </section >*/}

    </div >
  );
};

export default OriginalLandingPage; 
